import { type FC, type ReactNode, useState, useEffect } from "react";
import { X, ThumbsUp, ThumbsDown } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { cn } from "@/shared/lib/utils";
import { EventType, trackEvent } from "@/shared/utils/core";
import { useUserCustomizations } from "@/shared/hooks/useUserCustomizations";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { SupportAvatars } from "@/shared/components/SupportAvatars";
import { IconButton } from "@/shared/components/ui/icon-button";

interface LearnMoreSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

export const LearnMoreSheet: FC<LearnMoreSheetProps> = ({ open, onOpenChange, children }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
      document.body.style.overflow = "hidden";
      trackEvent(EventType.ONBOARDING_HELP_EXPANDED, {
        page_path: window.location.pathname,
      });
    } else {
      setVisible(false);
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open && !visible) return null;

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300",
          visible ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={() => onOpenChange(false)}
      />
      <div
        className={cn(
          "fixed inset-0 z-50 flex flex-col bg-white shadow-lg transition-transform duration-300 ease-in-out sm:left-auto sm:right-0 sm:top-0 sm:bottom-0 sm:w-96",
          visible ? "translate-x-0" : "translate-x-full",
        )}
      >
        <IconButton
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 z-10"
          label="Close"
          tooltip={false}
        >
          <X />
        </IconButton>
        {children}
      </div>
    </>
  );
};

interface LearnMoreHeaderProps {
  children: ReactNode;
  className?: string;
}

export const LearnMoreHeader: FC<LearnMoreHeaderProps> = ({ children, className }) => {
  return (
    <div
      className={cn("flex items-center border-b border-primary px-5 pt-5 pb-4 pr-14", className)}
    >
      {children}
    </div>
  );
};

interface LearnMoreTitleProps {
  children: ReactNode;
  className?: string;
}

export const LearnMoreTitle: FC<LearnMoreTitleProps> = ({ children, className }) => {
  return (
    <span className={cn("text-base font-medium leading-7 text-primary", className)}>
      {children}
    </span>
  );
};

interface LearnMoreContentProps {
  children: ReactNode;
  className?: string;
}

export const LearnMoreContent: FC<LearnMoreContentProps> = ({ children, className }) => {
  return <div className={cn("flex flex-1 flex-col overflow-y-auto", className)}>{children}</div>;
};

interface LearnMoreFooterProps {
  children: ReactNode;
  className?: string;
}

export const LearnMoreFooter: FC<LearnMoreFooterProps> = ({ children, className }) => {
  return <div className={cn("mt-auto", className)}>{children}</div>;
};

interface LearnMoreFeedbackProps {
  feedbackKey?: string;
  className?: string;
}

export const LearnMoreFeedback: FC<LearnMoreFeedbackProps> = ({ feedbackKey, className }) => {
  const { getCustomization, setCustomization } = useUserCustomizations();
  const key = feedbackKey ?? `learn-more-feedback-${window.location.pathname}`;
  const existingFeedback = getCustomization<string>(key);
  const [feedback, setFeedback] = useState<string | null>(existingFeedback);

  useEffect(() => {
    setFeedback(existingFeedback);
  }, [existingFeedback]);

  const handleFeedback = async (value: "good" | "bad") => {
    setFeedback(value);
    await setCustomization(key, value);
    enqueueSnackbar("Feedback received", { variant: "info" });
  };

  return (
    <div className={cn("flex flex-col gap-1 border-t border-primary px-5 py-4", className)}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary">Did this answer your question?</p>
        <TooltipProvider>
          <div className="flex gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => handleFeedback("good")}
                  className={cn(
                    "rounded-md p-1.5 transition-colors",
                    feedback === "good"
                      ? "bg-inverse text-white"
                      : "text-secondary hover:text-primary",
                  )}
                  aria-label="Helpful"
                >
                  <ThumbsUp className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Helpful</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => handleFeedback("bad")}
                  className={cn(
                    "rounded-md p-1.5 transition-colors",
                    feedback === "bad"
                      ? "bg-inverse text-white"
                      : "text-secondary hover:text-primary",
                  )}
                  aria-label="Unhelpful"
                >
                  <ThumbsDown className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Unhelpful</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>
    </div>
  );
};

export interface FaqItem {
  question: string;
  answer: ReactNode;
}

interface LearnMoreFAQProps {
  items: FaqItem[];
}

export const LearnMoreFAQ: FC<LearnMoreFAQProps> = ({ items }) => {
  return (
    <>
      {items.map((item, i) => (
        <div
          key={i}
          className={cn("px-5 pt-5 pb-5", i < items.length - 1 && "border-b border-primary")}
        >
          <p className="text-sm font-medium">{item.question}</p>
          <p className="mt-2 whitespace-pre-line text-sm text-secondary">{item.answer}</p>
        </div>
      ))}
    </>
  );
};

export const LearnMoreSupportButton: FC = () => {
  return (
    <button
      type="button"
      onClick={() => window.Intercom?.("show")}
      className="flex w-full items-center gap-3 border-t border-primary px-4 py-3 text-left transition-colors hover:bg-subtle"
    >
      <SupportAvatars size="md" showOnline />
      <div>
        <p className="text-sm font-semibold">Questions?</p>
        <p className="text-sm text-secondary">Chat with Elayne estate experts</p>
      </div>
    </button>
  );
};
